import { useState, useEffect, useRef } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import UnifiedDatePicker from "../../components/UnifiedDatePicker";
import { toast } from "react-hot-toast";
import { useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUser,
  faIdCard,
  faTag,
  faPhone,
  faBriefcase,
  faLocationDot,
  faCalendarAlt,
  faGraduationCap,
  faBuildingColumns,
  faFloppyDisk,
  faEdit,
  faPlusCircle,
  faCircleCheck,
  faHourglassHalf,
} from "@fortawesome/free-solid-svg-icons";
import Can from "../../permissions/Can";

export default function EditEmployee() {
  const { emp_id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  const mode = searchParams.get("mode");
  const rgmw = searchParams.get("rgmw");
  const isAddMode = mode === "add";

  const { register, handleSubmit, setValue, watch } = useForm();
  const [, setLoading] = useState(!isAddMode);
  const [error, setError] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [showOtherSchool, setShowOtherSchool] = useState(false);
  const [acceptInfo, setAcceptInfo] = useState(null); // { id, mgboul } سجل التنقل المعلق
  const originalRgmw = useRef(null);

  const schoolsData = useSelector((state) => state.main.data?.schoolData || []);
  const currentRgmw = watch("rgmw");
  const empmrkzValue = watch("empmrkz");
  const previousMarkaz = useRef(null);

  const schoolName =
    schoolsData.find((s) => s.rgmw === (currentRgmw || rgmw))?.schoolname ||
    "غير معروف";

  const filteredJobs = jobs.filter(
    (job) => String(job.Genderid) === String(watch("emp_gender"))
  );

  useEffect(() => {
    const fetchJobs = async () => {
      try {
        const response = await axios.get(
          "https://api.alfares.cloud/api/alfares/fetchJobs"
        );
        setJobs(response.data.jobs);
      } catch {}
    };
    fetchJobs();
  }, []);

  useEffect(() => {
    if (!isAddMode && emp_id) {
      const fetchEmployee = async () => {
        try {
          const credentials = JSON.parse(
            localStorage.getItem("userCredentials")
          );
          const token = credentials?.token;
          if (!token) {
            setError("التوكن مفقود. يرجى تسجيل الدخول.");
            setLoading(false);
            return;
          }

          const response = await axios.get(
            `https://api.alfares.cloud/api/alfares/getEmployee/${emp_id}/${token}`
          );
          const employeeData = response.data;

          Object.keys(employeeData).forEach((key) =>
            setValue(key, employeeData[key])
          );

          const formatDate = (d) => {
            if (!d || d === "null" || d === "undefined") return "";
            const date = new Date(d);
            return isNaN(date.getTime())
              ? ""
              : date.toISOString().split("T")[0];
          };

          setValue("hiring_date", formatDate(employeeData.hiring_date));
          setValue(
            "current_job_date",
            formatDate(employeeData.current_job_date)
          );

          previousMarkaz.current = employeeData.empmrkz?.toString();
          originalRgmw.current = employeeData.rgmw?.toString();

          // سجل التنقل المعلق (mgboul: 1 = بانتظار القبول)
          if (employeeData.tnglat_id) {
            setAcceptInfo({
              id: employeeData.tnglat_id,
              mgboul: Number(employeeData.tnglat_mgboul),
            });
          } else {
            setAcceptInfo(null);
          }
          // const isHalf = employeeData.empmrkz?.toString() === "0.5";
          const isHalf = parseFloat(employeeData.empmrkz) === 0.5;

          setShowOtherSchool(isHalf);
        } catch (err) {
          setError("حدث خطأ أثناء جلب بيانات الموظف");
        } finally {
          setLoading(false);
        }
      };

      fetchEmployee();
    } else {
      setValue("rgmw", rgmw);
      setShowOtherSchool(false);
      setLoading(false);
    }
  }, [emp_id, isAddMode, rgmw, setValue]);

  useEffect(() => {
    if (parseFloat(empmrkzValue) === 0.5) {
      setShowOtherSchool(true);
    } else {
      setShowOtherSchool(false);
      setValue("otherschool_rgmw", "");
    }
  }, [empmrkzValue, setValue]);


  // قبول الموظف المنقول في مدرسته الحالية (mgboul: 1 -> 0)
  const handleAcceptTransfer = async () => {
    if (!acceptInfo?.id) return;
    const confirmed = window.confirm(
      `قبول ${watch("emp_name") || "الموظف"} في ${schoolName}؟`
    );
    if (!confirmed) return;
    try {
      const credentials = JSON.parse(localStorage.getItem("userCredentials"));
      const token = credentials?.token;
      if (!token) {
        toast.error("التوكن مفقود. يرجى تسجيل الدخول من جديد.");
        return;
      }
      const t = toast.loading("جارٍ القبول...");
      await axios.put(
        `https://api.alfares.cloud/api/alfares/teachers_tnglat/${acceptInfo.id}/${token}`,
        { mgboul: 0 }
      );
      toast.dismiss(t);
      setAcceptInfo({ ...acceptInfo, mgboul: 0 });
      toast.success("تم قبول الموظف في المدرسة");
    } catch {
      toast.error("فشل القبول، حاول مجدداً");
    }
  };

  const onSubmit = async (data) => {
    try {
      const credentials = JSON.parse(localStorage.getItem("userCredentials"));
      const token = credentials?.token;
      if (!token) {
        toast.error("مفقود التوكن! يرجى تسجيل الدخول من جديد.");
        return;
      }

      // التحقق من نقل المدرسة
      const newRgmw = String(watch("rgmw") || "");
      const oldRgmw = String(originalRgmw.current || "");
      const isTransferring =
        !isAddMode && newRgmw && oldRgmw && newRgmw !== oldRgmw;

      if (isTransferring) {
        const newSchoolName =
          schoolsData.find((s) => String(s.rgmw) === newRgmw)?.schoolname ||
          "المدرسة الجديدة";
        const confirmed = window.confirm(
          `هل أنت متأكد من نقل الموظف إلى ${newSchoolName}؟`
        );
        if (!confirmed) return;
      }

      data.otherschool_rgmw = parseInt(watch("otherschool_rgmw")) || null;
      data.W_Num = parseInt(watch("W_Num")) || null;
      data.emp_gender = parseInt(watch("emp_gender")) || null;

      const formatDate = (val) => {
        if (!val || val === "") return null;
        const d = new Date(val);
        return isNaN(d.getTime()) ? null : d.toISOString().split("T")[0];
      };

      data.birth_date = formatDate(data.birth_date);
      data.hiring_date = formatDate(data.hiring_date);
      data.current_job_date = formatDate(data.current_job_date);

      // ✅ تنظيف الحقول الفارغة
      Object.keys(data).forEach((key) => {
        if (typeof data[key] === "string" && data[key].trim() === "") {
          data[key] = null;
        }
      });
      if (isAddMode) {
        await axios.post(
          `https://api.alfares.cloud/api/alfares/addEmployee/${rgmw}`,
          data,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("تمت الإضافة بنجاح");
      } else {
        await axios.put(
          `https://api.alfares.cloud/api/alfares/updateEmployee/${emp_id}/${token}`,
          data
        );
        toast.success(` تم تعديل بيانات: ${data.emp_name}`);

        if (parseFloat(empmrkzValue) === 0.5) {
          const res = await axios.get(
            `https://api.alfares.cloud/api/alfares/getEmployee/${emp_id}/${token}`
          );
          const empData = res.data;

          // عكس القيم
          empData.rgmw = watch("otherschool_rgmw"); // المدرسة المشتركة
          empData.otherschool_rgmw = watch("rgmw"); // المدرسة الحالية
          empData.W_Num = watch("W_Num");
          empData.emp_name = watch("emp_name");

          console.log("📦 بيانات ربط النصف مركز بعد العكس:", empData);

          await axios.put(
            `https://api.alfares.cloud/api/alfares/addOrUpdateHalfMrkaz/${emp_id}/${token}`,
            empData
          );
        }
      }

      navigate(-1);
    } catch (err) {
      console.error(
        "\u274C خطأ أثناء الحفظ:",
        err.response?.data || err.message
      );
      toast.error("❗ حدث خطأ أثناء الحفظ");
    }
  };

  if (error)
    return (
      <div className="error-state">
        <h3>{error}</h3>
      </div>
    );

  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      height: "44px",
      minHeight: "44px",
      background: "var(--panel-2)",
      borderColor: state.isFocused ? "var(--accent)" : "var(--border)",
      borderRadius: "6px",
      color: "var(--text)",
      fontFamily: "Cairo",
      boxShadow: state.isFocused ? "0 0 0 1px var(--accent)" : "none",
      display: "flex",
      alignItems: "center",
      transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
      "&:hover": {
        borderColor: state.isFocused
          ? "var(--accent)"
          : "var(--muted)",
      },
    }),
    valueContainer: (base) => ({
      ...base,
      display: "flex",
      alignItems: "center",
      height: "44px",
      padding: "0 12px",
    }),
    indicatorSeparator: () => ({
      display: "none",
    }),
    menuPortal: (base) => ({
      ...base,
      zIndex: 9999,
    }),
    menu: (base) => ({
      ...base,
      background: "var(--panel)",
      border: "1px solid var(--border)",
      borderRadius: "6px",
      overflow: "hidden",
      boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    }),
    option: (base, state) => ({
      ...base,
      background: state.isSelected
        ? "var(--accent)"
        : state.isFocused
        ? "color-mix(in srgb, var(--accent) 8%, transparent)"
        : "transparent",
      color: state.isSelected ? "#fff" : "var(--text)",
      cursor: "pointer",
      fontSize: "14px",
      fontFamily: "Cairo",
      textAlign: "right",
      "&:active": {
        background: "var(--accent)",
      },
    }),
    singleValue: (base) => ({
      ...base,
      color: "var(--text)",
      fontFamily: "Cairo",
      textAlign: "right",
      margin: 0,
      padding: 0,
    }),
    placeholder: (base) => ({
      ...base,
      color: "var(--muted)",
      fontSize: "14px",
      textAlign: "right",
      margin: 0,
      padding: 0,
    }),
    input: (base) => ({
      ...base,
      color: "var(--text)",
      textAlign: "right",
      margin: 0,
      padding: 0,
    }),
    dropdownIndicator: (base) => ({
      ...base,
      color: "var(--muted)",
      padding: "0 12px",
      "&:hover": {
        color: "var(--accent)",
      },
    }),
  };

  return (
    <div className="employee-edit-wrapper anim-fade-in">
      <div className="premium-edit-container">
        {/* Header Section */}
        <div className="premium-header-modern">
          <div className="header-left">
            <div className="header-icon-box">
              <FontAwesomeIcon icon={isAddMode ? faPlusCircle : faEdit} />
            </div>
            <div className="header-text-box">
              <h2>{isAddMode ? "إضافة موظف جديد" : "تعديل ملف الموظف"}</h2>
              <div className="breadcrumb-info">
                <span>نظام الفارس</span>
                <span className="separator">/</span>
                <span className="current-page">{schoolName}</span>
                {!isAddMode && (
                  <>
                    <span className="separator">|</span>
                    <span style={{ color: "var(--accent)" }}>
                      {watch("emp_name")}
                    </span>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* شريط حالة القبول للموظف المنقول */}
        {!isAddMode && acceptInfo && (
          <div
            className={
              acceptInfo.mgboul === 1 ? "accept-banner pending" : "accept-banner done"
            }
          >
            {acceptInfo.mgboul === 1 ? (
              <>
                <span className="accept-text">
                  <FontAwesomeIcon icon={faHourglassHalf} />
                  هذا الموظف في انتظار القبول في {schoolName}
                </span>
                <button
                  type="button"
                  className="accept-btn"
                  onClick={handleAcceptTransfer}
                >
                  <FontAwesomeIcon icon={faCircleCheck} />
                  قبول في المدرسة
                </button>
              </>
            ) : (
              <span className="accept-text">
                <FontAwesomeIcon icon={faCircleCheck} />
                مقبول في {schoolName}
              </span>
            )}
          </div>
        )}

        <div className="premium-card page-card" style={{ marginTop: "20px" }}>
          <form onSubmit={handleSubmit(onSubmit)} className="premium-form">
            {/* Section 1: Basic Info */}
            <div className="form-section">
              <h5 className="section-title">
                <FontAwesomeIcon icon={faUser} />
                البيانات الشخصية والأساسية
              </h5>
              <div className="section-grid">
                <div className="input-field span-2">
                  <label>اسم الموظف كاملاً</label>
                  <div className="input-wrapper">
                    <FontAwesomeIcon icon={faUser} className="field-icon" />
                    <input
                      {...register("emp_name")}
                      placeholder="أدخل الاسم رباعياً"
                      required
                    />
                  </div>
                </div>

                <div className="input-field">
                  <label>رقم الهوية</label>
                  <div className="input-wrapper">
                    <FontAwesomeIcon icon={faIdCard} className="field-icon" />
                    <input
                      {...register("emp_id")}
                      placeholder="12345678"
                      disabled={!isAddMode}
                    />
                  </div>
                </div>

                <div className="input-field">
                  <label>اسم الشهرة</label>
                  <div className="input-wrapper">
                    <FontAwesomeIcon icon={faTag} className="field-icon" />
                    <input
                      {...register("nickname")}
                      placeholder="مثلاً: أبو فلان"
                    />
                  </div>
                </div>

                <div className="input-field">
                  <label>رقم الهاتف</label>
                  <div className="input-wrapper">
                    <FontAwesomeIcon icon={faPhone} className="field-icon" />
                    <input {...register("phone1")} placeholder="07XXXXXXXX" />
                  </div>
                </div>

                <div className="input-field">
                  <label>الجنس</label>
                  <Select
                    options={[
                      { value: 1, label: "ذكر" },
                      { value: 2, label: "أنثى" },
                    ]}
                    value={[
                      { value: 1, label: "ذكر" },
                      { value: 2, label: "أنثى" },
                    ].find((opt) => opt.value === watch("emp_gender"))}
                    onChange={(selected) =>
                      setValue("emp_gender", selected?.value || "")
                    }
                    placeholder="اختر.."
                    styles={customSelectStyles}
                    isRtl={true}
                    isSearchable={false}
                    menuPortalTarget={document.body}
                  />
                </div>

                <div className="input-field">
                  <label>مكان الولادة</label>
                  <div className="input-wrapper">
                    <FontAwesomeIcon
                      icon={faLocationDot}
                      className="field-icon"
                    />
                    <input
                      {...register("place_birth")}
                      placeholder="المدينة/القرية"
                    />
                  </div>
                </div>

                <div className="input-field">
                  <label>تاريخ الميلاد</label>
                  <UnifiedDatePicker
                    value={watch("birth_date")}
                    onChange={(date) => setValue("birth_date", date)}
                    portalId="root"
                  />
                </div>
              </div>
            </div>

            {/* Section 2: Career Information */}
            <div className="form-section accent-border">
              <h5 className="section-title">
                <FontAwesomeIcon icon={faBriefcase} />
                الحالة الوظيفية والتعيين
              </h5>
              <div className="section-grid three-cols">
                <div className="input-field">
                  <label>المسمى الوظيفي</label>
                  <Select
                    options={filteredJobs.map((job) => ({
                      value: job.WathID,
                      label: job.W_Name,
                    }))}
                    value={filteredJobs
                      .map((job) => ({ value: job.WathID, label: job.W_Name }))
                      .find((opt) => opt.value === watch("W_Num"))}
                    onChange={(selected) =>
                      setValue("W_Num", selected?.value || "")
                    }
                    placeholder="ابحث عن وظيفة.."
                    styles={customSelectStyles}
                    isRtl={true}
                    menuPortalTarget={document.body}
                  />
                </div>

                <div className="input-field">
                  <label>Item Number</label>
                  <div className="input-wrapper">
                    <FontAwesomeIcon
                      icon={faBriefcase}
                      className="field-icon"
                    />
                    <input
                      {...register("itemnumber")}
                      placeholder="الرقم المتسلسل"
                    />
                  </div>
                </div>

                <div className="input-field">
                  <label>صنف الوظيفة</label>
                  <Select
                    options={[
                      { value: 1, label: "مصنف ومثبت" },
                      { value: 2, label: "مصنف غير مثبت" },
                      { value: 3, label: "عقد سنوي" },
                      { value: 4, label: "بديل" },
                      { value: 5, label: "مياومة" },
                    ]}
                    value={[
                      { value: 1, label: "مصنف ومثبت" },
                      { value: 2, label: "مصنف غير مثبت" },
                      { value: 3, label: "عقد سنوي" },
                      { value: 4, label: "بديل" },
                      { value: 5, label: "مياومة" },
                    ].find((opt) => opt.value === watch("job_senf"))}
                    onChange={(selected) =>
                      setValue("job_senf", selected?.value || "")
                    }
                    placeholder="اختر الصنف.."
                    styles={customSelectStyles}
                    isRtl={true}
                    menuPortalTarget={document.body}
                  />
                </div>

                <div className="input-field">
                  <label>تاريخ التعيين الرسمي</label>
                  <UnifiedDatePicker
                    value={watch("hiring_date")}
                    onChange={(date) => setValue("hiring_date", date)}
                    portalId="root"
                  />
                </div>

                <div className="input-field">
                  <label>تاريخ الوظيفة الحالية</label>
                  <UnifiedDatePicker
                    value={watch("current_job_date")}
                    onChange={(date) => setValue("current_job_date", date)}
                    portalId="root"
                  />
                </div>

                <div className="input-field">
                  <label>نوع المركز</label>
                  <Select
                    options={[
                      { value: "1", label: "مركز كامل" },
                      { value: "0.5", label: "نصف مركز" },
                    ]}
                    value={
                      String(watch("empmrkz")) === "0.5"
                        ? { value: "0.5", label: "نصف مركز" }
                        : { value: "1", label: "مركز كامل" }
                    }
                    onChange={(selected) =>
                      setValue("empmrkz", selected?.value || "")
                    }
                    styles={customSelectStyles}
                    isRtl={true}
                    isSearchable={false}
                    menuPortalTarget={document.body}
                  />
                </div>

                {showOtherSchool && (
                  <div className="input-field span-3">
                    <label>المدرسة الأخرى المشتركة</label>
                    <Select
                      options={schoolsData
                        .filter((s) => s.rgmw !== (currentRgmw || rgmw))
                        .map((s) => ({ value: s.rgmw, label: s.schoolname }))}
                      value={schoolsData
                        .map((s) => ({ value: s.rgmw, label: s.schoolname }))
                        .find((opt) => opt.value === watch("otherschool_rgmw"))}
                      onChange={(selected) =>
                        setValue("otherschool_rgmw", selected?.value || "")
                      }
                      placeholder="اختر المدرسة الأخرى.."
                      styles={customSelectStyles}
                      isRtl={true}
                      menuPortalTarget={document.body}
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Section 3: Academic Qualifications */}
            <div className="form-section">
              <h5 className="section-title">
                <FontAwesomeIcon icon={faGraduationCap} />
                المؤهلات العلمية والدراسية
              </h5>
              <div className="section-grid">
                <div className="input-field">
                  <label>المؤهل العلمي</label>
                  <div className="input-wrapper">
                    <FontAwesomeIcon
                      icon={faGraduationCap}
                      className="field-icon"
                    />
                    <input
                      {...register("qualification1")}
                      placeholder="مثلاً: بكالوريوس"
                    />
                  </div>
                </div>

                <div className="input-field">
                  <label>التخصص</label>
                  <div className="input-wrapper">
                    <FontAwesomeIcon icon={faTag} className="field-icon" />
                    <input
                      {...register("specialization")}
                      placeholder="التخصص الدقيق"
                    />
                  </div>
                </div>

                <div className="input-field">
                  <label>الجامعة / الكلية</label>
                  <div className="input-wrapper">
                    <FontAwesomeIcon
                      icon={faBuildingColumns}
                      className="field-icon"
                    />
                    <input
                      {...register("university")}
                      placeholder="اسم الجامعة"
                    />
                  </div>
                </div>

                <div className="input-field">
                  <label>سنة التخرج</label>
                  <div className="input-wrapper">
                    <FontAwesomeIcon
                      icon={faCalendarAlt}
                      className="field-icon"
                    />
                    <input
                      {...register("graduation_year")}
                      placeholder="YYYY"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Section 4: Transfer School (Edit mode only) */}
            {!isAddMode && (
              <div className="form-section">
                <h5 className="section-title">
                  <FontAwesomeIcon icon={faBuildingColumns} />
                  نقل الموظف إلى مدرسة أخرى
                </h5>
                <div className="section-grid">
                  <div className="input-field span-2">
                    <label>المدرسة الحالية</label>
                    <div className="input-wrapper">
                      <FontAwesomeIcon
                        icon={faBuildingColumns}
                        className="field-icon"
                      />
                      <input
                        value={schoolName}
                        readOnly
                        disabled
                        style={{ cursor: "default", opacity: 0.8 }}
                      />
                    </div>
                  </div>
                  <div className="input-field span-2">
                    <label>المدرسة الجديدة (اختر للنقل)</label>
                    <Select
                      options={schoolsData
                        .filter(
                          (s) =>
                            String(s.rgmw) !== String(originalRgmw.current)
                        )
                        .map((s) => ({ value: s.rgmw, label: s.schoolname }))}
                      value={schoolsData
                        .map((s) => ({ value: s.rgmw, label: s.schoolname }))
                        .find(
                          (opt) =>
                            String(opt.value) === String(currentRgmw) &&
                            String(opt.value) !== String(originalRgmw.current)
                        )}
                      onChange={(selected) => {
                        if (selected) {
                          setValue("rgmw", selected.value, {
                            shouldDirty: true,
                          });
                          toast.success(
                            `تم اختيار ${selected.label} — احفظ لنقل الموظف`
                          );
                        } else {
                          // إلغاء الاختيار = الرجوع للمدرسة الأصلية
                          setValue("rgmw", originalRgmw.current, {
                            shouldDirty: true,
                          });
                        }
                      }}
                      placeholder="ابحث واختر المدرسة الجديدة.."
                      styles={customSelectStyles}
                      isRtl={true}
                      isClearable={true}
                      menuPortalTarget={document.body}
                    />
                    {currentRgmw &&
                      String(currentRgmw) !== String(originalRgmw.current) && (
                        <div
                          style={{
                            marginTop: "8px",
                            fontSize: "12px",
                            color: "#d97706",
                            fontFamily: "Cairo, sans-serif",
                          }}
                        >
                          سيتم نقل الموظف إلى{" "}
                          {
                            schoolsData.find(
                              (s) => String(s.rgmw) === String(currentRgmw)
                            )?.schoolname
                          }{" "}
                          عند الحفظ
                        </div>
                      )}
                  </div>
                </div>
              </div>
            )}

            {/* Save Action */}
            <div className="form-actions">
              <Can module="employees" action={isAddMode ? "add" : "edit"}>
                <button
                  type="submit"
                  className="premium-submit-btn"
                  style={{ fontFamily: "Cairo, sans-serif" }}
                >
                <div className="btn-content">
                  <FontAwesomeIcon icon={faFloppyDisk} />
                  <span>
                    {isAddMode
                      ? "إضافة ملف الموظف"
                      : "اعتماد التعديلات وحفظ الموظف"}
                  </span>
                </div>
                <div className="btn-glow"></div>
              </button>
              </Can>
            </div>
          </form>
        </div>
      </div>

      <style>{`
        .employee-edit-wrapper {
          padding: 20px 0;
          color: var(--text);
          display: flex;
          justify-content: center;
        }

        .accept-banner {
          margin-top: 16px;
          padding: 12px 18px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          flex-wrap: wrap;
          font-size: 14px;
        }
        .accept-banner.pending {
          background: #fef3c7;
          border: 1px solid #f59e0b;
          color: #92400e;
        }
        .accept-banner.done {
          background: #ecfdf5;
          border: 1px solid #10b981;
          color: #065f46;
          justify-content: center;
        }
        .accept-banner .accept-text {
          display: flex;
          align-items: center;
          gap: 8px;
          font-weight: 600;
        }
        .accept-banner .accept-btn {
          background: #10b981;
          color: #fff;
          border: none;
          border-radius: 6px;
          padding: 10px 22px;
          font-size: 14px;
          font-family: Cairo;
          cursor: pointer;
          display: flex;
          align-items: center;
          gap: 8px;
          transition: background 0.2s;
        }
        .accept-banner .accept-btn:hover {
          background: #059669;
        }

        .premium-edit-container {
          width: 100%;
          max-width: 900px;
          margin: 0 auto;
        }

        /* Modern Header */
        .premium-header-modern {
          display: flex;
          align-items: center;
          justify-content: space-between;
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: 6px;
          padding: 16px 20px;
          margin-bottom: 16px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .header-icon-box {
          width: 40px;
          height: 40px;
          background: color-mix(in srgb, var(--accent) 12%, transparent);
          border: 1px solid color-mix(in srgb, var(--accent) 25%, transparent);
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          color: var(--accent);
          flex-shrink: 0;
        }

        .header-text-box h2 {
          margin: 0 0 4px 0;
          font-size: 17px;
          font-weight: 400;
          color: var(--text);
          font-family: "Cairo", sans-serif;
        }

        .breadcrumb-info {
          display: flex;
          align-items: center;
          gap: 8px;
          font-size: 12px;
          color: var(--muted);
          flex-wrap: wrap;
        }

        .separator {
          color: var(--border);
          font-size: 11px;
        }

        .current-page {
          color: var(--accent);
          font-weight: 400;
        }

        /* Premium Form Card */
        .premium-card {
          background: var(--panel);
          border: 1px solid var(--border);
          border-radius: 6px;
          padding: 24px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
        }

        .form-section {
          margin-bottom: 24px;
          padding-bottom: 24px;
          border-bottom: 1px solid var(--border);
        }

        .form-section:last-child {
          margin-bottom: 0;
          padding-bottom: 0;
          border-bottom: none;
        }

        .section-title {
          font-size: 15px;
          font-weight: 400;
          color: var(--text);
          margin-bottom: 16px;
          display: flex;
          align-items: center;
          gap: 8px;
          font-family: "Cairo", sans-serif;
        }

        .section-title svg {
          color: var(--accent);
        }

        .section-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 16px;
        }

        .span-2 {
          grid-column: span 2;
        }

        @media (max-width: 768px) {
          .employee-edit-wrapper {
            padding: 12px 0;
          }
          .premium-header-modern {
            padding: 12px 14px;
            margin-bottom: 12px;
          }
          .header-text-box h2 {
            font-size: 15px;
          }
          .breadcrumb-info {
            font-size: 11px;
          }
          .premium-card {
            padding: 16px 14px;
          }
          .section-grid {
            grid-template-columns: 1fr;
            gap: 12px;
          }
          .span-2 {
            grid-column: span 1;
          }
          .form-section {
            margin-bottom: 18px;
            padding-bottom: 18px;
          }
        }

        .input-field label {
          display: block;
          margin-bottom: 6px;
          font-size: 13px;
          color: var(--muted);
          font-weight: 400;
          font-family: "Cairo", sans-serif;
        }

        .input-wrapper {
          position: relative;
          display: flex;
          align-items: center;
        }

        .field-icon {
          position: absolute;
          right: 12px;
          color: var(--muted);
          font-size: 14px;
          z-index: 1;
          pointer-events: none;
        }

        .input-wrapper input,
        .input-wrapper select {
          width: 100%;
          height: 44px;
          background: var(--panel-2);
          border: 1px solid var(--border);
          border-radius: 6px;
          padding: 0 38px 0 12px;
          color: var(--text);
          font-size: 14px;
          font-family: "Cairo", sans-serif;
          transition: border-color 0.2s, box-shadow 0.2s;
          box-sizing: border-box;
        }

        .input-wrapper input:focus,
        .input-wrapper select:focus {
          border-color: var(--accent);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--accent) 12%, transparent);
          outline: none;
          background: var(--panel);
        }

        .input-wrapper input:disabled,
        .input-wrapper select:disabled {
          background: var(--panel-2);
          color: var(--muted);
          cursor: not-allowed;
          opacity: 0.7;
        }

        .form-actions {
          display: flex;
          justify-content: flex-end;
          gap: 10px;
          margin-top: 24px;
          padding-top: 16px;
          border-top: 1px solid var(--border);
        }

        @media (max-width: 768px) {
          .form-actions {
            flex-direction: column;
          }
          .premium-submit-btn {
            width: 100%;
          }
        }

        .premium-submit-btn {
          background: var(--accent);
          color: #fff;
          border: none;
          border-radius: 6px;
          padding: 12px 28px;
          font-size: 14px;
          font-weight: 400;
          font-family: "Cairo", sans-serif;
          cursor: pointer;
          transition: background 0.2s, transform 0.15s;
          box-shadow: 0 2px 8px color-mix(in srgb, var(--accent) 30%, transparent);
        }

        .premium-submit-btn:hover {
          background: #1d7567;
          transform: translateY(-1px);
        }

        .premium-submit-btn:active {
          transform: translateY(0);
        }

        .btn-content {
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }
      `}</style>
    </div>
  );
}
